/**
 * @file components/common/thinkingParser.js
 * @description Parses chain-of-thought / reasoning blocks that models embed inside
 * their output using XML-style tags.
 *
 * Known tags emitted by models in this app:
 *   <think>        — DeepSeek R1, Qwen3 (reasoning mode), Kimi K2 Thinking
 *   <thinking>     — Various open-source reasoning fine-tunes
 *   <reasoning>    — Some model variants (mentioned by Moonshot / Kimi docs)
 *   <reason>       — Smaller instruction-tuned models
 *   <scratchpad>   — Some CoT fine-tuned models
 *   <reflection>   — Reflection-focused models
 */

const THINKING_TAG_NAMES = ['think', 'thinking', 'reasoning', 'reason', 'scratchpad', 'reflection'];

// Matches a complete <tag>...</tag> block (non-greedy, case-insensitive).
const COMPLETE_BLOCK_REGEX = new RegExp(
    `<(${THINKING_TAG_NAMES.join('|')})[^>]*>([\\s\\S]*?)<\\/\\1>`,
    'gi'
);

// Matches an opening tag (for in-progress streaming detection).
const OPEN_TAG_REGEX = new RegExp(
    `<(${THINKING_TAG_NAMES.join('|')})[^>]*>`,
    'i'
);

// Matches the FIRST orphan closing tag — no matching open tag precedes it.
// Used for DeepSeek R1: the backend injects <think> into the prompt, so the
// model's streamed output starts INSIDE the block and only emits </think>.
const CLOSE_TAG_REGEX = new RegExp(
    `<\\/(${THINKING_TAG_NAMES.join('|')})>`,
    'i'
);

/**
 * Parses a message string and separates thinking/reasoning content from the
 * main visible content.
 *
 * Handles three cases:
 *   A) Complete blocks:  <think>…</think>   — standard models
 *   B) Orphan close tag: …</think>          — DeepSeek R1 (open tag was in the prompt)
 *   C) Unclosed open tag: <think>…          — in-progress streaming
 *
 * @param {string} text - Raw message text, potentially containing thinking tags.
 * @returns {{
 *   content: string,          // Text with all thinking blocks removed
 *   thinkingBlocks: string[], // Extracted thinking content (one entry per block)
 *   isThinking: boolean       // True when a thinking tag is open but not yet closed
 * }}
 */
export function parseThinkingContent(text) {
    if (!text) return { content: '', thinkingBlocks: [], isThinking: false };

    const thinkingBlocks = [];
    let working = text;

    // ── Step 1: Extract COMPLETE blocks (both open and close tag present). ──────
    working = working.replace(COMPLETE_BLOCK_REGEX, (_match, _tag, inner) => {
        const trimmed = inner.trim();
        if (trimmed) thinkingBlocks.push(trimmed);
        return '';
    });

    // ── Step 2: Handle ORPHAN closing tag (DeepSeek R1 case). ──────────────────
    // After step 1 any remaining </tag> has no matching open tag in the text —
    // meaning the opening tag was injected into the prompt by the backend.
    // Everything before that first orphan close tag is thinking content.
    const closeMatch = CLOSE_TAG_REGEX.exec(working);
    if (closeMatch) {
        const before = working.slice(0, closeMatch.index).trim();
        const after  = working.slice(closeMatch.index + closeMatch[0].length).trim();
        if (before) thinkingBlocks.push(before);
        working = after;
    }

    // ── Step 3: Detect an IN-PROGRESS (unclosed open) thinking block. ──────────
    // Relevant during streaming: open tag arrived but close tag has not yet.
    let isThinking = false;
    const lastOpenMatch = [...working.matchAll(new RegExp(OPEN_TAG_REGEX.source, 'gi'))].pop();
    if (lastOpenMatch) {
        const tagName = lastOpenMatch[1];
        const closeTag = `</${tagName}>`;
        const afterOpen = working.slice(lastOpenMatch.index + lastOpenMatch[0].length);
        if (!afterOpen.toLowerCase().includes(closeTag.toLowerCase())) {
            isThinking = true;
            working = working.slice(0, lastOpenMatch.index);
        }
    }

    return {
        content: working.trim(),
        thinkingBlocks,
        isThinking,
    };
}
